import { createContext, ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastMessage } from "../components/Toast";
import { TokenDecode } from "../utils/token-decode";
import { api } from "./../services/api";
import { IToastType } from "./Interfaces";
import { airbrakeService } from "../services/airbrake";

type SignInCredentials = {
  user: string;
  password: string;
};

type User = {
  user?: string;
  sectorId?: number;
  idUsuario?: number;
  CODEMP?: any;
  userName?: string;
};

type AuthContextData = {
  signIn(credentials: SignInCredentials): Promise<void>;
  isAuthenticated: boolean;
  authenticateUser?: User;
  loading: boolean;
  logoff(): void;
  tokenExpiration?: number | null;
};

export const AuthContext = createContext({} as AuthContextData);

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [authenticateUser, setAuthenticatedUser] = useState<User>();
  const isAuthenticated = !!authenticateUser;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessageType, setToastMessageType] = useState<IToastType>(
    IToastType.unknow
  );
  const [toastMessage, setToastMessage] = useState("");
  const [tokenExpiration, setTokenExpiration] = useState<number | null>(null);

  useEffect(() => {
    const tokenFromLocalStorage = localStorage.getItem("apontamento.jwt");
    if (tokenFromLocalStorage) {
      api.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${tokenFromLocalStorage}`;
      const tokenDecoder = new TokenDecode(tokenFromLocalStorage);
      const idUsuario = tokenDecoder.getIdUsuario();
      const CODEMP = localStorage.getItem("apontamento.CODEMP");
      const userName = tokenDecoder.getUserName();
      const expiration = tokenDecoder.getTokenExpiration();
      
      setTokenExpiration(expiration);
      setAuthenticatedUser((state) => ({
        ...state,
        idUsuario,
        CODEMP,
        userName,
      }));
    }
    setLoading(false);
  }, []);

  // Monitor token expiration
  useEffect(() => {
    if (!tokenExpiration || !isAuthenticated) return;

    const checkTokenExpiration = () => {
      const now = Date.now();
      if (now >= tokenExpiration) {
        console.log('Token expirado em:', new Date(tokenExpiration).toLocaleString());
        console.log('Fazendo logout automático devido à expiração do token');
        logoff();
      }
    };

    // Check immediately
    checkTokenExpiration();

    // Set up interval to check every minute
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);
  }, [tokenExpiration, isAuthenticated]);

  async function signIn({ user, password }: SignInCredentials) {
    if (user !== "" && password !== "") {
      const response = await api
        .post("authenticate", { user, password })
        .catch(() => {
          setShowToast(true);
          setToastMessageType(IToastType.warning);
          setToastMessage("Login Inválido!");
        });
      console.log(response);
      if (response?.data?.jwt) {
        localStorage.setItem("apontamento.jwt", response?.data?.jwt);
        localStorage.setItem("apontamento.CODEMP", response?.data?.CODEMP);
        const tokenDecoder = new TokenDecode(response.data.jwt);
        const idUsuario = tokenDecoder.getIdUsuario();
        const userName = tokenDecoder.getUserName();
        const expiration = tokenDecoder.getTokenExpiration();
        
        setTokenExpiration(expiration);
        const userData = {
          user,
          idUsuario,
          CODEMP: response?.data?.CODEMP,
          userName,
        };
        setAuthenticatedUser(userData);
        
        // Set user context in Airbrake
        airbrakeService.setUser({
          id: idUsuario,
          name: userName,
          username: user,
          company: response?.data?.CODEMP
        });
        navigate("/products", {
          state: {
            jwt: response.data.jwt,
          },
        });
      } else {
        setShowToast(true);
        setToastMessageType(IToastType.warning);
        setToastMessage("Login Inválido!");
      }
    } else {
      setShowToast(true);
      setToastMessageType(IToastType.warning);
      setToastMessage("Login Inválido!");
    }
  }

  const logoff = () => {
    localStorage.removeItem("apontamento.jwt");
    setTokenExpiration(null);
    navigate("/");
  };

  if (loading) {
    return <></>;
  }

  return (
    <AuthContext.Provider
      value={{ signIn, isAuthenticated, authenticateUser, loading, logoff, tokenExpiration }}
    >
      <ToastMessage
        setShowToast={setShowToast}
        showToast={showToast}
        toastMessage={toastMessage}
        toastMessageType={toastMessageType}
      />
      {children}
    </AuthContext.Provider>
  );
}
